import type { ShipFrameDef } from "./schemas";

export const SHIP_FRAMES: Record<string, ShipFrameDef> = {
  skimmer: {
    id: "skimmer",
    name: "Skimmer",
    role: "Balanced scout",
    drawback: "Modest cargo bay",
    hull: 100,
    shield: 60,
    speedMult: 1,
    cargoMult: 1,
  },
  hauler: {
    id: "hauler",
    name: "Hauler",
    role: "Salvage runner",
    drawback: "Slow cruise",
    hull: 120,
    shield: 50,
    speedMult: 0.88,
    cargoMult: 1.45,
  },
  interceptor: {
    id: "interceptor",
    name: "Interceptor",
    role: "Fast strike",
    drawback: "Thin hull",
    hull: 75,
    shield: 55,
    speedMult: 1.28,
    cargoMult: 0.8,
  },
  bulwark: {
    id: "bulwark",
    name: "Bulwark",
    role: "Frontline tank",
    drawback: "Slow boost recovery",
    hull: 150,
    shield: 90,
    speedMult: 0.78,
    cargoMult: 1.1,
  },
  vanguard: {
    id: "vanguard",
    name: "Vanguard",
    role: "Heavy gunship",
    drawback: "Slower weapon cycle",
    hull: 135,
    shield: 70,
    speedMult: 0.9,
    cargoMult: 1,
    weaponDamageMult: 1.12,
    weaponCooldownMult: 1.08,
  },
  reclaimer: {
    id: "reclaimer",
    name: "Reclaimer",
    role: "Field refinery",
    drawback: "Reduced weapon output",
    hull: 115,
    shield: 60,
    speedMult: 0.92,
    cargoMult: 1.25,
    weaponDamageMult: 0.95,
    recycleMult: 3,
  },
  corsair: {
    id: "corsair",
    name: "Corsair",
    role: "Raid capacitor",
    drawback: "Power falls with an empty hold",
    hull: 82,
    shield: 48,
    speedMult: 1.12,
    cargoMult: 0.85,
    weaponDamageMult: 0.98,
    cargoDamagePerSalvage: 0.001,
    cargoDamageCap: 0.25,
  },
  jackal: {
    id: "jackal",
    name: "Jackal",
    role: "Kill-fed pursuit craft",
    drawback: "Thin shields and a small cargo hold",
    hull: 88,
    shield: 42,
    speedMult: 1.16,
    cargoMult: 0.9,
    weaponDamageMult: 1.04,
    killBoostRestore: 0.12,
  },
  fluxrunner: {
    id: "fluxrunner",
    name: "Fluxrunner",
    role: "Boost-shield interceptor",
    drawback: "Burns boost fuel 50% faster; reduced weapon output",
    hull: 95,
    shield: 110,
    speedMult: 1.18,
    cargoMult: 0.9,
    weaponDamageMult: 0.96,
    boostShieldRegenPerSecond: 18,
    boostFuelDrainMult: 1.5,
  },
  slipstream: {
    id: "slipstream",
    name: "Slipstream",
    role: "Boost-weaving strike craft",
    drawback: "Light hull, small cargo bay, and reduced base weapon output",
    hull: 78,
    shield: 52,
    speedMult: 1.24,
    cargoMult: 0.75,
    weaponDamageMult: 0.96,
    boostCycleReduction: 0.35,
    boostCycleProcCooldownS: 1.5,
  },
  backlash: {
    id: "backlash",
    name: "Backlash",
    role: "Shield-break counterstrike craft",
    drawback: "Thin hull, light shields, and reduced cargo capacity",
    hull: 76,
    shield: 46,
    speedMult: 1.17,
    cargoMult: 0.72,
    weaponDamageMult: 1.03,
    shieldBreakCycleReduction: 0.65,
    shieldBreakCycleProcCooldownS: 8,
  },
  magpie: {
    id: "magpie",
    name: "Magpie",
    role: "Salvage-fed ordnance runner",
    drawback: "Light plating and reduced base weapon output",
    hull: 86,
    shield: 58,
    speedMult: 1.1,
    cargoMult: 1.18,
    weaponDamageMult: 0.97,
    salvageOrdnanceCycleReduction: 0.35,
  },
  breachwing: {
    id: "breachwing",
    name: "Breachwing",
    role: "Shield-break cycle striker",
    drawback: "Light shield reserve and reduced cargo capacity",
    hull: 92,
    shield: 50,
    speedMult: 1.12,
    cargoMult: 0.82,
    enemyShieldBreakCycleReduction: 0.2,
  },
  tetherwing: {
    id: "tetherwing",
    name: "Tetherwing",
    role: "Recall-cycle pursuit craft",
    drawback: "Light armor, reduced weapon output, and a small cargo hold",
    hull: 86,
    shield: 54,
    speedMult: 1.2,
    cargoMult: 0.76,
    weaponDamageMult: 0.98,
    projectileRecallCycleReduction: 0.3,
    projectileRecallBoostRestore: 0.12,
  },
};

/** Frame-native weapon multiplier, including cargo-powered raid capacitors. */
export function frameCargoDamageBonus(frame: ShipFrameDef, carriedSalvage: number): number {
  return Math.min(
    frame.cargoDamageCap ?? 0,
    Math.max(0, carriedSalvage) * (frame.cargoDamagePerSalvage ?? 0),
  );
}

export function frameWeaponDamageMultiplier(frame: ShipFrameDef, carriedSalvage: number): number {
  return (frame.weaponDamageMult ?? 1) * (1 + frameCargoDamageBonus(frame, carriedSalvage));
}
